/**
 * shop.vendor_claim_tokens service layer.
 *
 * Two surfaces use these helpers:
 *
 *  - `apps/admin` — platform admins manually create vendor shells, issue
 *    shareable claim URLs, revoke them, and view the audit list of past
 *    tokens. All of these go through SECURITY DEFINER RPCs in the
 *    `shop` schema (`rpc_admin_*`).
 *
 *  - `apps/sell` (`/claim/<token>`) — a prospective vendor previews the
 *    profile and finishes the claim. These go through the
 *    `shop-vendor-claim` edge function which wraps the anon-callable
 *    SECURITY DEFINER RPCs in `app_public`.
 *
 * The plaintext token is NEVER persisted anywhere; the issuing RPC
 * returns it exactly once (`adminIssueShopVendorClaimToken`) and the
 * admin UI is responsible for surfacing the copy-once warning.
 */

import { supabase, supabaseEndpoint } from '../client';
import { applyKeyset, buildNextCursor, clampPageSize, selectCountOption, type KeysetInput, type ListResult } from '../pagination';
import type {
  ShopVendor,
  ShopVendorClaimToken,
  ShopVendorClaimStatus,
} from './shopTypes';

// =========================================================================
// Admin: create vendor manually
// =========================================================================

export interface AdminCreateShopVendorInput {
  companyName: string;
  slug: string;
  supportEmail?: string;
  publicHeadline?: string;
  publicAboutMd?: string;
  publicLogoUrl?: string;
  logoUrl?: string;
  /** ISO2 lower-cased; matches `shop.vendors.inventory_country_iso2`. */
  inventoryCountryIso2?: string;
  /** ISO2 codes; matches `shop.vendors.ships_to_country_iso2`. */
  shipsToCountryIso2?: string[];
  /** Members of `shop.payment_method`. */
  paymentMethods?: ShopVendor['payment_methods'];
}

/**
 * Calls `shop.rpc_admin_create_vendor`. Returns the new vendor row.
 * Platform-admin only; non-admin callers get a 42501 error from the RPC.
 */
export async function adminCreateShopVendor(
  input: AdminCreateShopVendorInput,
): Promise<ShopVendor> {
  const payload: Record<string, unknown> = {};
  if (input.publicHeadline) payload.public_headline = input.publicHeadline;
  if (input.publicAboutMd) payload.public_about_md = input.publicAboutMd;
  if (input.publicLogoUrl) payload.public_logo_url = input.publicLogoUrl;
  if (input.logoUrl) payload.logo_url = input.logoUrl;
  if (input.inventoryCountryIso2)
    payload.inventory_country_iso2 = input.inventoryCountryIso2;
  if (input.shipsToCountryIso2 && input.shipsToCountryIso2.length > 0)
    payload.ships_to_country_iso2 = input.shipsToCountryIso2;
  if (input.paymentMethods && input.paymentMethods.length > 0)
    payload.payment_methods = input.paymentMethods;

  const { data, error } = await (supabase() as unknown as {
    schema: (s: string) => {
      rpc: (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: unknown; error: { message: string } | null }>;
    };
  })
    .schema('shop')
    .rpc('rpc_admin_create_vendor', {
      p_company_name: input.companyName,
      p_slug: input.slug.toLowerCase(),
      p_support_email: input.supportEmail ?? null,
      p_payload: payload,
    });
  if (error) throw new Error(error.message);
  return data as ShopVendor;
}

// =========================================================================
// Admin: claim token lifecycle
// =========================================================================

/** Result returned from `shop.rpc_admin_issue_vendor_claim_token`. */
export interface AdminIssueClaimTokenResult {
  /** Token row id. */
  token_id: string;
  /** PLAINTEXT token — visible exactly once. */
  token: string;
  expires_at: string;
  vendor_id: string;
}

export interface AdminIssueClaimTokenInput {
  vendorId: string;
  /**
   * Postgres `interval` literal (e.g. `'30 days'`, `'1 hour'`). When
   * omitted the RPC defaults to 30 days. Pass `'100 years'` for the
   * "never expires" option.
   */
  expiresIn?: string;
  emailTo?: string;
}

/**
 * Generates a fresh single-use claim token for the vendor, superseding
 * any prior outstanding token. The plaintext token is returned ONCE —
 * the admin UI must surface a "copy this now" warning because
 * subsequent reads only see the sha256 hash.
 */
export async function adminIssueShopVendorClaimToken(
  input: AdminIssueClaimTokenInput,
): Promise<AdminIssueClaimTokenResult> {
  const { data, error } = await (supabase() as unknown as {
    schema: (s: string) => {
      rpc: (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: unknown; error: { message: string } | null }>;
    };
  })
    .schema('shop')
    .rpc('rpc_admin_issue_vendor_claim_token', {
      p_vendor_id: input.vendorId,
      p_expires_in: input.expiresIn ?? '30 days',
      p_email_to: input.emailTo ?? null,
    });
  if (error) throw new Error(error.message);
  return data as AdminIssueClaimTokenResult;
}

export interface AdminRevokeClaimTokenInput {
  tokenId: string;
  reason?: string;
}

export async function adminRevokeShopVendorClaimToken(
  input: AdminRevokeClaimTokenInput,
): Promise<ShopVendorClaimToken> {
  const { data, error } = await (supabase() as unknown as {
    schema: (s: string) => {
      rpc: (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: unknown; error: { message: string } | null }>;
    };
  })
    .schema('shop')
    .rpc('rpc_admin_revoke_vendor_claim_token', {
      p_token_id: input.tokenId,
      p_reason: input.reason ?? null,
    });
  if (error) throw new Error(error.message);
  return data as ShopVendorClaimToken;
}

// =========================================================================
// Admin: read helpers (claim status + token history)
// =========================================================================

export async function getShopVendorClaimStatus(
  vendorId: string,
): Promise<ShopVendorClaimStatus | null> {
  const { data, error } = await (supabase() as unknown as {
    schema: (s: string) => {
      from: (t: string) => {
        select: (cols: string) => {
          eq: (
            k: string,
            v: string,
          ) => { maybeSingle: () => Promise<{ data: unknown; error: { message: string } | null }> };
        };
      };
    };
  })
    .schema('shop')
    .from('v_vendor_claim_status')
    .select('*')
    .eq('vendor_id', vendorId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data ?? null) as ShopVendorClaimStatus | null;
}

export interface ListShopVendorClaimTokensInput extends KeysetInput {
  vendorId: string;
}

/**
 * Token-history view for the admin vendor-detail page. Keyset-paginated
 * on `issued_at desc` (per ai-rules/pagination-required.md), capped at
 * the standard page size via `clampPageSize`.
 */
export async function listShopVendorClaimTokens(
  input: ListShopVendorClaimTokensInput,
): Promise<ListResult<ShopVendorClaimToken>> {
  const pageSize = clampPageSize(input.pageSize);
  // Build the chain in a single fluent expression so the paginationLint
  // static analyzer can see the `.limit(pageSize)` terminal sitting at
  // the end of the .from('vendor_claim_tokens').select(...) chain.
  // applyKeyset returns the same query object (PostgrestFilterBuilder)
  // back so it composes inline.
  const base = (supabase() as any)
    .schema('shop')
    .from('vendor_claim_tokens')
    .select('*', selectCountOption(input.countMode))
    .eq('vendor_id', input.vendorId)
    .order('id', { ascending: false });
  const keyed = applyKeyset(base, input.cursor, 'issued_at');
  const { data, error, count } = await keyed.limit(pageSize);
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as ShopVendorClaimToken[];
  return {
    rows,
    nextCursor: buildNextCursor(
      rows as unknown as Array<Record<string, unknown>>,
      pageSize,
      'issued_at',
    ),
    total: typeof count === 'number' ? count : null,
    totalIsExact: input.countMode === 'exact',
  };
}

// =========================================================================
// Public (anonymous): edge-function-backed lookup + redeem
// =========================================================================

export interface ClaimTokenPreview {
  claimable: boolean;
  vendor_id?: string;
  company_name?: string;
  slug?: string;
  public_headline?: string | null;
  public_logo_url?: string | null;
  expires_at?: string;
  email_sent_to?: string | null;
}

async function callClaimEdgeFn(
  action: 'lookup' | 'redeem',
  body: Record<string, unknown>,
): Promise<unknown> {
  const { url, anonKey } = supabaseEndpoint();
  const resp = await fetch(
    `${url}/functions/v1/shop-vendor-claim?action=${action}`,
    {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        apikey: anonKey,
        // Always send the anon key as the bearer; the function is
        // verify_jwt=false so this is purely to satisfy the project
        // gateway, not the function logic.
        authorization: `Bearer ${anonKey}`,
      },
      body: JSON.stringify({ ...body, action }),
    },
  );
  let payload: unknown;
  try {
    payload = await resp.json();
  } catch {
    throw new Error(`shop-vendor-claim returned non-JSON (${resp.status})`);
  }
  if (!resp.ok) {
    const msg =
      (payload as { message?: string; error?: string })?.message ??
      (payload as { error?: string })?.error ??
      `shop-vendor-claim failed (${resp.status})`;
    throw new Error(msg);
  }
  return payload;
}

export async function lookupShopVendorClaimToken(
  token: string,
): Promise<ClaimTokenPreview> {
  const payload = (await callClaimEdgeFn('lookup', { token })) as ClaimTokenPreview;
  return payload ?? { claimable: false };
}

export interface RedeemShopVendorClaimInput {
  token: string;
  email: string;
  password: string;
  fullName?: string;
}

export interface RedeemShopVendorClaimSession {
  access_token: string;
  refresh_token: string;
  expires_in: number | null;
  token_type: string;
}

export type RedeemShopVendorClaimResult =
  | {
      ok: true;
      vendor_id: string;
      slug: string;
      session: RedeemShopVendorClaimSession;
      user: { id: string; email: string };
    }
  | {
      ok: false;
      /**
       * Stable machine-readable error codes returned by the redeem RPC
       * + edge function. Used by the claim page UI to decide whether
       * to flip to a "sign in instead" affordance, prompt for a
       * different email, etc.
       */
      error:
        | 'invalid'
        | 'expired'
        | 'revoked'
        | 'already_claimed'
        | 'email_mismatch'
        | 'weak_password'
        | 'sign_in_failed'
        | 'account_exists'
        | 'unauthenticated';
      message: string;
    };

export async function redeemShopVendorClaimToken(
  input: RedeemShopVendorClaimInput,
): Promise<RedeemShopVendorClaimResult> {
  const payload = (await callClaimEdgeFn('redeem', {
    token: input.token,
    email: input.email,
    password: input.password,
    full_name: input.fullName ?? '',
  })) as RedeemShopVendorClaimResult;
  return payload;
}

// =========================================================================
// Pure UI helper: should the buyer message-vendor surface go inert?
// =========================================================================

/**
 * Returns true when buyer-side contact affordances ("Message vendor",
 * "Contact vendor", etc.) should be rendered disabled because the
 * vendor has not yet claimed their PeptideClients account and cannot
 * receive replies.
 *
 * Unclaimed vendors live in three observable states:
 *   1. `status` is 'draft' or 'pending_claim' (admin built it, no claim
 *      has happened yet) — clearly inert.
 *   2. `status` is 'claimed' but no further admin moderation has been
 *      done — these vendors CAN receive messages (the human is here).
 *   3. `status` is one of the legacy values ('pending', 'approved',
 *      'suspended', 'archived') AND `claimed_at` is null. These predate
 *      this feature and represent real self-signup vendors that already
 *      have a `shop.vendor_users` owner. Treat them as claimed — the
 *      backfill stamps `claimed_at` for those rows, so this branch is
 *      reached only when both signals are missing AND the row is
 *      brand-new. We choose "send" over "inert" here because the more
 *      conservative inert default would erase the existing buyer flow
 *      for every legacy vendor on the first deploy before the backfill
 *      ran. The backfill in the same migration eliminates that race.
 *
 * Pure function: takes only the minimal vendor shape the call sites
 * need (status + claimed_at), so unit tests can exercise every branch
 * without spinning up Supabase.
 */
export function isShopVendorUnclaimed(
  vendor: Pick<ShopVendor, 'status' | 'claimed_at'> | null | undefined,
): boolean {
  if (!vendor) return false;
  if (vendor.status === 'draft' || vendor.status === 'pending_claim') {
    return true;
  }
  return false;
}
